import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useAuth } from '../../../context/AuthContext';
import { getRequestsByRealtor } from '../../../services/serviceRequestService';
import { SERVICE_CATEGORIES } from '../../../services/customerFormService';

const currentYear = new Date().getFullYear();
const YEARS = [currentYear, currentYear - 1, currentYear - 2];
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
const STATUS_CHIPS = [
  { label: '완료 전체', key: 'all_complete' },
  { label: '완료', key: 'completed' },
  { label: '정산완료', key: 'settlement_done' },
];
const VIEW_TABS = ['일별', '월별'];

const CATEGORY_COLORS = ['#4A6CF7', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899'];
const SVC_LABEL = SERVICE_CATEGORIES.reduce((acc, s) => ({ ...acc, [s.key]: s.label }), {});

const extractRegion = (address) => {
  if (!address) return '미상';
  const head = address.trim().split(/\s+/)[0] || '';
  if (!head) return '미상';
  return head.replace(/(특별시|광역시|특별자치시|특별자치도)$/, '');
};

const requestDate = (r) => {
  const ts = r.created_at;
  if (ts?.toDate) return ts.toDate();
  if (ts?.seconds) return new Date(ts.seconds * 1000);
  if (typeof r.createdAt === 'string' && /^\d{4}-/.test(r.createdAt)) return new Date(r.createdAt);
  return null;
};

const CustomerDbStats = () => {
  const { userData } = useAuth();
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [statusIdx, setStatusIdx] = useState(0);
  const [viewTab, setViewTab] = useState(0);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userData?.uid) {
      setLoading(false);
      return;
    }
    getRequestsByRealtor(userData.uid)
      .then((rows) => setRequests(rows || []))
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  }, [userData?.uid]);

  const filtered = useMemo(() => {
    const status = STATUS_CHIPS[statusIdx].key;
    return requests.filter((r) => {
      const d = requestDate(r);
      if (!d) return false;
      if (viewTab === 0) {
        if (d.getFullYear() !== Number(year) || d.getMonth() + 1 !== Number(month)) return false;
      } else {
        if (d.getFullYear() !== Number(year)) return false;
      }
      const s = r.hq_status || r.hqStatus || '';
      if (status === 'completed') return r.serviceCompleted || s === 'completed' || s === 'settlement_done';
      if (status === 'settlement_done') return s === 'settlement_done';
      // all_complete: 의미 있는 모든 진행 상태
      return r.serviceCompleted || s === 'completed' || s === 'settlement_done' || s === 'settlement_check' || s === 'assigned';
    });
  }, [requests, year, month, statusIdx, viewTab]);

  const categoryData = useMemo(() => {
    const counts = {};
    filtered.forEach((r) => {
      const cat = r.category || 'etc';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([key, value]) => ({ name: SVC_LABEL[key] || key, value }))
      .sort((a, b) => b.value - a.value);
  }, [filtered]);

  const regionData = useMemo(() => {
    const counts = {};
    filtered.forEach((r) => {
      const region = extractRegion(r.address || r.moving_address || '');
      counts[region] = (counts[region] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([region, count]) => ({ region, count }))
      .sort((a, b) => b.count - a.count);
  }, [filtered]);

  const total = filtered.length;
  const isEmpty = !loading && total === 0;

  return (
    <Container>
      <ChipRow>
        {YEARS.map((y) => (
          <Chip key={y} $active={year === y} onClick={() => setYear(y)}>
            {y}년
          </Chip>
        ))}
      </ChipRow>

      {viewTab === 0 && (
        <MonthRow>
          {MONTHS.map((m) => (
            <MonthChip key={m} $active={month === m} onClick={() => setMonth(m)}>
              {m}월
            </MonthChip>
          ))}
        </MonthRow>
      )}

      <ChipRow>
        {STATUS_CHIPS.map((s, i) => (
          <Chip key={s.key} $active={statusIdx === i} onClick={() => setStatusIdx(i)}>
            {s.label}
          </Chip>
        ))}
      </ChipRow>

      <TabRow>
        {VIEW_TABS.map((t, i) => (
          <Tab key={t} $active={viewTab === i} onClick={() => setViewTab(i)}>
            {t}
          </Tab>
        ))}
      </TabRow>

      <SummaryRow>
        <SummaryLabel>총 건수</SummaryLabel>
        <SummaryValue>{total}건</SummaryValue>
      </SummaryRow>

      {loading ? (
        <EmptyBox>
          <EmptyIcon>…</EmptyIcon>
          <EmptyTitle>불러오는 중...</EmptyTitle>
        </EmptyBox>
      ) : isEmpty ? (
        <EmptyBox>
          <EmptyIcon>!</EmptyIcon>
          <EmptyTitle>{viewTab === 0 ? '일별' : '월별'} 실적이 없어요</EmptyTitle>
          <EmptyDesc>선택한 연·월에 고객 DB 실적이 없습니다.</EmptyDesc>
        </EmptyBox>
      ) : (
        <>
          <SectionLabel>카테고리별 분포</SectionLabel>
          <ChartCard>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={categoryData}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={80}
                  label={(entry) => entry.name}
                >
                  {categoryData.map((entry, idx) => (
                    <Cell key={entry.name} fill={CATEGORY_COLORS[idx % CATEGORY_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => `${v}건`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          <SectionLabel style={{ marginTop: 24 }}>지역별 분포</SectionLabel>
          <RegionList>
            {regionData.map((r) => (
              <RegionRow key={r.region}>
                <RegionName>{r.region}</RegionName>
                <RegionCount>{r.count}건</RegionCount>
              </RegionRow>
            ))}
          </RegionList>
        </>
      )}
    </Container>
  );
};

export default CustomerDbStats;

const Container = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ChipRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.base};
  flex-wrap: wrap;
`;

const Chip = styled.button`
  padding: 8px 16px;
  border-radius: 20px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  background: ${({ $active, theme }) => $active ? theme.colors.primary : '#fff'};
  color: ${({ $active }) => $active ? '#fff' : '#1F2937'};
  border: ${({ $active, theme }) => $active ? 'none' : `1px solid ${theme.colors.border}`};
`;

const MonthRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const MonthChip = styled.button`
  padding: 6px 12px;
  border-radius: 16px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  background: ${({ $active, theme }) => $active ? theme.colors.primary : '#fff'};
  color: ${({ $active }) => $active ? '#fff' : '#6B7280'};
  border: ${({ $active, theme }) => $active ? 'none' : `1px solid ${theme.colors.border}`};
`;

const TabRow = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const Tab = styled.button`
  flex: 1;
  padding: 10px 0;
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ $active, theme }) => $active ? theme.fontWeight.semibold : theme.fontWeight.regular};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.textSecondary};
  background: none;
  border: none;
  border-bottom: 2px solid ${({ $active, theme }) => $active ? theme.colors.primary : 'transparent'};
  cursor: pointer;
  margin-bottom: -1px;
`;

const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.base};
  background: ${({ theme }) => theme.colors.primary}10;
  border-radius: ${({ theme }) => theme.radius.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SummaryLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const SummaryValue = styled.span`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

const SectionLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ChartCard = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.md};
`;

const RegionList = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
`;

const RegionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.base};
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};

  &:first-of-type { border-top: none; }
`;

const RegionName = styled.span`
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
`;

const RegionCount = styled.span`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

const EmptyBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xxxl} 0;
`;

const EmptyIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.colors.bg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSize.xl};
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const EmptyTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const EmptyDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
`;
